import { useState, useEffect, useCallback } from "react";
import { Instagram, Plus, Trash2, Edit, ExternalLink, Heart, GripVertical, Image, Save, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstagramManagePage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ image_url: "", caption_ar: "", caption_en: "", instagram_url: "", likes: 0, order: 0 });
  const [imagePreview, setImagePreview] = useState("");
  const token = localStorage.getItem("token");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/instagram/feed`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPosts(await res.json());
    } catch (err) { console.error("Operation error:", err); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API}/website/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setForm(f => ({ ...f, image_url: url }));
        setImagePreview(url);
        toast.success("تم رفع الصورة");
      }
    } catch (err) { console.error(err); toast.error("فشل رفع الصورة"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error("يرجى رفع صورة");
      return;
    }
    try {
      if (editing) {
        const res = await fetch(`${API}/instagram/posts/${editing}`, {
          method: "PUT", headers, body: JSON.stringify(form)
        });
        if (res.ok) toast.success("تم التحديث");
      } else {
        const res = await fetch(`${API}/instagram/posts`, {
          method: "POST", headers, body: JSON.stringify({ ...form, order: posts.length })
        });
        if (res.ok) toast.success("تمت الإضافة");
      }
      resetForm();
      fetchPosts();
    } catch (err) { console.error(err); toast.error("حدث خطأ"); }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ image_url: "", caption_ar: "", caption_en: "", instagram_url: "", likes: 0, order: 0 });
    setImagePreview("");
  };

  const editPost = (post) => {
    setForm({
      image_url: post.image_url,
      caption_ar: post.caption_ar || "",
      caption_en: post.caption_en || "",
      instagram_url: post.instagram_url || "",
      likes: post.likes || 0,
      order: post.order || 0
    });
    setImagePreview(post.image_url);
    setEditing(post.id);
    setShowForm(true);
  };

  const deletePost = async (id) => {
    if (!window.confirm("هل تريد حذف هذا المنشور؟")) return;
    try {
      await fetch(`${API}/instagram/posts/${id}`, { method: "DELETE", headers });
      toast.success("تم الحذف");
      fetchPosts();
    } catch (err) { console.error(err); toast.error("حدث خطأ"); }
  };

  return (
    <div className="space-y-6" data-testid="instagram-manage-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Instagram className="w-7 h-7 text-pink-600" />
            إدارة انستغرام
          </h1>
          <p className="text-gray-500 text-sm mt-1">أضف منشورات انستغرام لعرضها على الموقع</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity text-sm font-medium"
          data-testid="add-instagram-post-btn"
        >
          <Plus className="w-4 h-4" />
          إضافة منشور
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-100 rounded-xl p-4">
        <p className="text-sm text-pink-800">
          <strong>ملاحظة:</strong> أضف صور من حساب انستغرام العيادة مع رابط المنشور الأصلي. هذه الصور ستظهر في قسم انستغرام على الموقع الرئيسي.
        </p>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 border border-gray-200 space-y-4" data-testid="instagram-form">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900">{editing ? "تعديل المنشور" : "إضافة منشور جديد"}</h3>
            <button type="button" onClick={resetForm} className="p-1.5 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">الصورة</label>
            <div className="flex items-start gap-4">
              {imagePreview ? (
                <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-gray-200">
                  <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => { setImagePreview(""); setForm(f => ({ ...f, image_url: "" })); }}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-pink-400 hover:bg-pink-50 transition-colors">
                  <Image className="w-6 h-6 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">رفع صورة</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="instagram-image-upload" />
                </label>
              )}
              <div className="flex-1 space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">رابط المنشور على انستغرام (اختياري)</label>
                  <input
                    type="url"
                    value={form.instagram_url}
                    onChange={e => setForm({ ...form, instagram_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="https://www.instagram.com/p/..."
                    dir="ltr"
                    data-testid="instagram-url-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">عدد الإعجابات</label>
                  <input
                    type="number"
                    value={form.likes}
                    onChange={e => setForm({ ...form, likes: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="instagram-likes-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">الوصف بالعربي</label>
              <textarea
                value={form.caption_ar}
                onChange={e => setForm({ ...form, caption_ar: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-20 resize-none"
                placeholder="وصف المنشور بالعربي..."
                data-testid="instagram-caption-ar"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">الوصف بالإنجليزي</label>
              <textarea
                value={form.caption_en}
                onChange={e => setForm({ ...form, caption_en: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-20 resize-none"
                placeholder="English caption..."
                dir="ltr"
                data-testid="instagram-caption-en"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg text-sm hover:bg-pink-700" data-testid="save-instagram-post-btn">
              <Save className="w-4 h-4" />
              {editing ? "تحديث" : "إضافة"}
            </button>
            <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* Posts Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">جاري التحميل...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Instagram className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">لا توجد منشورات بعد</p>
          <p className="text-gray-400 text-sm mt-1">أضف منشورات من حساب انستغرام العيادة</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden group" data-testid={`instagram-post-${post.id}`}>
              <div className="relative aspect-square">
                <img src={post.image_url} alt={post.caption_ar} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => editPost(post)}
                      className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-blue-50"
                      data-testid={`edit-post-${post.id}`}
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={() => deletePost(post.id)}
                      className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-50"
                      data-testid={`delete-post-${post.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                    {post.instagram_url && (
                      <a
                        href={post.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-pink-50"
                      >
                        <ExternalLink className="w-4 h-4 text-pink-600" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm text-gray-900 line-clamp-2">{post.caption_ar || "بدون وصف"}</p>
                <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                  <Heart className="w-3.5 h-3.5 text-red-400" />
                  <span>{post.likes || 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InstagramManagePage;
